import { CheckboxInputControlProps, CustomNumberSelectInputControlProps, DataFieldType, FormFieldsInputControlProps, InputControlProps, LinkInputControlProps, SelectDataType, SelectInputControlProps, SwitchInputControlProps } from "@/types/formTypes"
import { FormControl, FormLabel, FormErrorMessage, Input, InputGroup, InputRightElement, Button, Icon, Td, Th, Tr, HStack, Select, Text, useBoolean, Box, TableContainer, Table, Tbody, Link, Flex, Checkbox, Accordion, AccordionItem, AccordionButton, AccordionPanel, Heading, FormHelperText, Radio, Textarea, Switch, Badge } from "@chakra-ui/react"
import { DurationUnit, RateUnit, SharedUserUnit } from "@prisma/client"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { BsEyeFill, BsFillEyeSlashFill } from "react-icons/bs"
import CodeHighlighting from "../../CodeHighlighting"
import { makeId } from "@/lib/stringGenerator"
import NextLink from "next/link"
import { UseFormSetValue } from "react-hook-form"
import { MdOutlineFileUpload } from "react-icons/md"
import Image from "next/image"

export function InputControl({ field, fieldFormat = field, label, action, getValues, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: InputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)
  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={isRequired}>
          <FormLabel>{label ?? fieldFormatUpper}</FormLabel>
          <Input
            id={field + action}
            {...register(field, { ...validation, setValueAs: v => rest?.type == "number" ? Number(v) : v?.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;') })}
            {...rest}
          />
          <FormErrorMessage>
            {/* {!!error && error.message} AND */}
            {!!errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <Tr>
          <Th>{label ?? fieldFormatUpper}</Th>
          <Td>{!!getValues && getValues(field)?.toString()}</Td>
        </Tr>
      )}
    </>
  )
}

export function TextareaInputControl({ field, fieldFormat = field, label, action, getValues, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: InputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)
  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={isRequired}>
          <FormLabel>{label ?? fieldFormatUpper}</FormLabel>
          <Textarea
            id={field + action}
            {...register(field, { ...validation, setValueAs: v => rest?.type == "number" ? Number(v) : v?.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;') })}
          />
          <FormErrorMessage>
            {/* {!!error && error.message} AND */}
            {!!errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <Tr>
          <Th>{label ?? fieldFormatUpper}</Th>
          <Td>{!!getValues && getValues(field)?.toString()}</Td>
        </Tr>
      )}
    </>
  )
}

export function SwitchInputControl({ field, fieldFormat = field, label, action, getValues, watch, isFalseTextValue, isTrueTextValue, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: SwitchInputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)
  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={isRequired}>
          <FormLabel htmlFor='email-alerts' mb='0'>{label ?? fieldFormatUpper}</FormLabel>
          {/* @ts-ignore: "Switch value property has type conflict" */}
          <Switch 
            id={field + action}
            {...register(field, { ...validation })}
            {...rest}
          />
          <FormErrorMessage>
            {/* {!!error && error.message} AND */}
            {!!errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <Tr>
          <Th>Status</Th>
          <Td>{getValues?.(field) ? <Badge colorScheme="green">{isTrueTextValue}</Badge> : <Badge colorScheme='orange'>{isFalseTextValue}</Badge>} </Td>
        </Tr>
      )}
    </>
  )
}

export function PasswordInputControl({ field, fieldFormat = field, action, getValues, label, type = "text", isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: InputControlProps) {
  const [showPassword, setShowPassword] = useState(false)
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)
  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={isRequired}>
          <FormLabel>{label ?? fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)}</FormLabel>
          <InputGroup size='md'>
            <Input
              id={field + action}
              pr='4.5rem'
              type={showPassword ? 'text' : 'password'}
              {...register(field, { ...validation, setValueAs: v => v?.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;') })}
              {...rest}
            />
            <InputRightElement width='4.5rem'>
              <Button h='1.75rem' size='sm' onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <Icon as={BsEyeFill} /> : <Icon as={BsFillEyeSlashFill} />}
              </Button>
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>
            {errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <Tr>
          <Th>{label ?? fieldFormatUpper}</Th>
          <Td><InputGroup size='md'>
            <Input
              id={field + action}
              pr='4.5rem'
              type={showPassword ? 'text' : 'password'}
              isReadOnly
              {...register(field, { ...validation })}
              {...rest}
            />
            <InputRightElement width='4.5rem'>
              <Button h='1.75rem' size='sm' onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <Icon as={BsEyeFill} /> : <Icon as={BsFillEyeSlashFill} />}
              </Button>
            </InputRightElement>
          </InputGroup></Td>
        </Tr>
      )}
    </>

  )
}

export function SelectInputControl({ options, isStacked = false, field, fieldFormat = field, label, action, getValues, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: SelectInputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)
  console.log('From select field: ', options);

  const filteredOption: SelectDataType = getValues ? (options.filter(o => o.id == getValues(field)))[0] : {}

  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={isRequired}>
          {!isStacked && <FormLabel>{label ?? fieldFormatUpper}</FormLabel>}
          <Select
            id={field + action}
            variant={isStacked ? "filled" : "outline"}
            {...register(field, { ...validation, setValueAs: v => field !== 'role' && field !== 'paymentGate' ? parseInt(v) : v })}
            defaultValue={options[0]?.id}
          >
            {options.map(o => <option key={o.id} value={o.id}>{typeof o !== undefined && 'name' in o ? o?.name : ""}</option>)}
          </Select>
          <FormErrorMessage>
            {!!errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <>
          {isStacked ? getValues : (
            <Tr>
              <Th>{label ?? fieldFormatUpper}</Th>
              <Td>{!!getValues && !!filteredOption && 'name' in filteredOption && filteredOption?.name}</Td>
            </Tr>
          )}
        </>
      )}
    </>
  )
}

export function CheckboxInputControl({ options, isStacked = false, field, fieldFormat = field, label, action, getValues, watch, setValue, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: CheckboxInputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)

  console.log("from checkbox: ", getValues?.(field));

  let getValuesData: any = []

  if(typeof getValues?.(field) === "object") {
    getValuesData = [...getValues?.(field)]
  } else {
    getValuesData = [getValues?.(field)]
  }
  

  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={isRequired}>
          {!isStacked && <FormLabel>{label ?? fieldFormatUpper}</FormLabel>}
          <Flex direction="column">
            {options.map(o => (
              <Checkbox
                key={o.id}
                id={field + action + o.id}
                value={o.id}
                {...register(field, { ...validation })}
                defaultChecked={(getValuesData?.filter((v: any) => v?.id == o?.id))[0]}
              >
                {typeof o !== undefined && 'name' in o ? o?.name : ""}
              </Checkbox>
            ))}
          </Flex>
          <FormErrorMessage>
            {!!errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <>
          {isStacked ? getValues : (
            <Tr>
              <Th>{label ?? fieldFormatUpper}</Th>
              <Td>
                <ul>
                  {!!getValues && (getValues(field).map((v: any) => (
                    <li key={v.id}>
                      {v.name}
                    </li>
                  )))}
                </ul>
              </Td>
            </Tr>
          )}
        </>
      )}
    </>
  )
}

interface Duration {
  name: string;
  value: DurationUnit
}

interface Rate {
  name: string;
  value: RateUnit
}

interface Shared {
  name: string;
  value: SharedUserUnit
}


export function CustomNumberSelectInputControl({ field, fieldFormat = field, selectField, fieldType, label, action, getValues, watch, setValue, isRequired = true, register, errors, validation, ...rest }: CustomNumberSelectInputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)

  const [fieldIsRequired, setfieldIsRequired] = useState(isRequired)

  const durationUnit: Duration[] = [
    { name: "Seconde(s)", value: "s" },
    { name: "Minute(s)", value: "m" },
    { name: "Heure(s)", value: "h" },
    { name: "Jour(s)", value: "d" },
    { name: "Semaine(s)", value: "w" },
    { name: "Illimité", value: "unlimited" }
  ]

  const rateUnit: Rate[] = [
    { name: "B", value: "B" },
    { name: "KB", value: "KB" },
    { name: "MB", value: "MB" },
    { name: "GB", value: "GB" },
    { name: "Illimité", value: "unlimited" }
  ]

  const ratePerSecondUnit: Rate[] = [
    { name: "bps", value: "B" },
    { name: "kbps", value: "KB" },
    { name: "mbps", value: "MB" },
    // { name: "gbps", value: "GB" },
    { name: "Illimité", value: "unlimited" }
  ]

  const sharedUnit: Shared[] = [
    { name: "maximum", value: "maximum" },
    { name: "Illimité", value: "unlimited" }
  ]

  const watch_selectField = watch(selectField)
  useEffect(() => {
    if (watch_selectField == "unlimited") {
      setValue(field, '')
      setfieldIsRequired(false)
    } else {
      setfieldIsRequired(true)
    }
  }, [watch, watch_selectField, field, setValue])
  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={fieldIsRequired}>
          <FormLabel>{label ?? fieldFormatUpper}</FormLabel>
          <HStack>
            <Input
              id={field + action}
              type="number"
              min="0"
              disabled={watch(selectField) === "unlimited"}
              // required
              {...register(field, { required: fieldIsRequired ? 'Veuillez renseigner votre ' + fieldFormat : false, ...validation, setValueAs: v => Number(v) })}
              {...rest}
            />

            <Select
              id={field + action}
              width="50%"
              variant="filled"
              defaultValue="unlimited"
              {...register(selectField, { required: fieldIsRequired ? 'Veuillez renseigner votre ' + fieldFormat : false, ...validation })}
            >
              {fieldType === "duration" && (
                <>
                  {durationUnit.map(d => <option key={d.value} value={d.value}> {d.name} </option>)}
                </>
              )}
              {fieldType === "rate" && (
                <>
                  {rateUnit.map(r => <option key={r.value} value={r.value}> {r.name} </option>)}
                </>
              )}
              {fieldType === "shared" && (
                <>
                  {sharedUnit.map(s => <option key={s.value} value={s.value}> {s.name} </option>)}
                </>
              )}
              {fieldType === "ratePerSecond" && (
                <>
                  {ratePerSecondUnit.map(s => <option key={s.value} value={s.value}> {s.name} </option>)}
                </>
              )}
            </Select>

          </HStack>
          <FormErrorMessage>
            {!!errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <Tr>
          <Th>{label ?? fieldFormatUpper}</Th>
          {fieldType === "ratePerSecond" ? <Td>{getValues && getValues(selectField) !== "unlimited" ? (getValues(field) ?? "0") + '' + getValues(selectField).toString().toLowerCase() + 'ps' : (getValues && getValues(selectField).toString().toLowerCase())}</Td> : <Td>{getValues && getValues(selectField) !== "unlimited" ? (getValues(field) ?? "0") + '' + getValues(selectField) : (getValues && getValues(selectField))}</Td>}
        </Tr>
      )}
    </>
  )
}

export function NumberSelectInputControl1({ field, fieldFormat = field, label, action, getValues, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: InputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)
  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={isRequired}>
          <FormLabel>{label ?? fieldFormatUpper}</FormLabel>
          <HStack>
            <Input
              id={field + action}
              type="number"
              min="0"
              // disabled={selectValue=="illimité"}
              required
              // value={data}
              // onChange={(e:any) => setData(e.target.value)}
              {...register(field, { ...validation })}
              {...rest}
            />

            <Select
              width="50%"
              variant="filled"
            // value={selectValue} 
            // onChange={(e:any) => setSelectValue(e.target.value)}
            >

            </Select>

          </HStack>
          <FormErrorMessage>
            {!!errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <Tr>
          <Th>{label ?? fieldFormatUpper}</Th>
          <Td>{!!getValues && getValues(field)}</Td>
        </Tr>
      )}
    </>
  )
}

export function PrivateIpInputControl({ field, fieldFormat = field, label, action, getValues, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: InputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)
  const [privateIp, setPrivateIp] = useBoolean(!!getValues && !!getValues('vpn_ip') ? true : false)
  const [vpnClientUser, setVpnClientUser] = useState(!!getValues && getValues('vpn_user') ? getValues('vpn_user') : makeId(15))
  const [vpnClientpass, setVpnClientpass] = useState(!!getValues && getValues('vpn_password') ? getValues('vpn_password') : makeId(15))
  const [command, setCommand] = useState(` /interface ovpn-client add cipher="aes256" connect-to="${process?.env?.NEXT_PUBLIC_USERMAN_DOMAIN}" name="mp_ovpn_client" user="${vpnClientUser}" password="${vpnClientpass}" port="1200" comment="mp_config_client" `)
  return (
    // <>
    //   {!privateIp ? (
    //     <>
    //       {action !== "show" ? (
    //         <>

    //           <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={isRequired}>
    //             <FormLabel>{label ?? fieldFormatUpper}</FormLabel>
    //             <Input
    //               // value={privateIp ? !!getValues && getValues(field) : ""}
    //               id={field + action}
    //               {...register(field, { ...validation, setValueAs: v => v?.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;') })}
    //               {...rest}
    //             />
    //             <FormErrorMessage>
    //               {/* {!!error && error.message} AND */}
    //               {!!errors[field] && errors[field].message}
    //             </FormErrorMessage>
    //           </FormControl>

    //         </>
    //       ) : (
    //         <Tr>
    //           <Th>{label ?? fieldFormatUpper}</Th>
    //           <Td>{!!getValues && getValues(field)?.toString()}</Td>
    //         </Tr>
    //       )}
    //     </>) : (
    <>
      {action === 'show' ? (
        <Tr>
          <Td colSpan={2}>
            <Box my="2" mt="5" >
              <Input id="vpn_client" type="hidden" value={privateIp.toString()}
                {...register("vpn_client", { setValueAs: v => v == "true" ? true : false })}
              />
              <Input id="vpn_user" type="hidden" value={vpnClientUser}
                {...register("vpn_user")}
              />
              <Input id="vpn_password" type="hidden" value={vpnClientpass}
                {...register("vpn_password")}
              />
              <Text fontWeight="medium" mb="1">Veuillez coller ce code dans le terminal du routeur:</Text>
              <CodeHighlighting text={command} />
            </Box>
          </Td>
        </Tr>
      ) : (
        <Box my="2" mt="5" >
          <Input id="vpn_client" type="hidden" value={privateIp.toString()}
            {...register("vpn_client", { setValueAs: v => v == "true" ? true : false })}
          />
          <Input id="vpn_user" type="hidden" value={vpnClientUser}
            {...register("vpn_user")}
          />
          <Input id="vpn_password" type="hidden" value={vpnClientpass}
            {...register("vpn_password")}
          />
          <Text fontWeight="medium" mb="1">Veuillez coller ce code dans le terminal du routeur:</Text>
          <CodeHighlighting text={command} />
        </Box>
      )

      }
    </>
    //   )}

    //   {action !== "show" && <>
    //     {privateIp ? <Button colorScheme="brand" variant="link" onClick={() => setPrivateIp.off()}>{"J'ai une addresse IP publique"}</Button> : <Button colorScheme="brand" variant="link" onClick={() => setPrivateIp.on()}>{"J'ai pas d'adresse IP publique"}</Button>}
    //   </>}
    // </>
  )
}

export function LinkInputControl({ field, fieldFormat = field, label, action, getValues, linkStart, linkEnd, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: LinkInputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)
  const currentValue = !!getValues && getValues(field)?.toString()
  // const pathname = use
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL
  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field + '-link'} isInvalid={!!errors[field]} isRequired={isRequired}>
          <FormLabel>{label ?? fieldFormatUpper}</FormLabel>
          <Input
            id={field + action + '-link'}
            {...register(field, { ...validation, setValueAs: v => v?.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;') })}
            {...rest}
          />
          <FormErrorMessage>
            {/* {!!error && error.message} AND */}
            {!!errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <Tr>
          <Th>{label ?? fieldFormatUpper}</Th>
          <Td> <Link colorScheme="brand" as={NextLink} href={`${!!linkStart && '/' + linkStart}${currentValue && '/' + currentValue}${!!linkEnd && '/' + linkEnd}`} target="_blank"> {`${baseUrl}${!!linkStart && '/' + linkStart}${!!currentValue && '/' + currentValue}${!!linkEnd && '/' + linkEnd}`} </Link> </Td>
        </Tr>
      )}
    </>
  )
}


export default function TableWrapper({ children }: { children: React.ReactNode }) {
  return (
    <TableContainer >
      <Table variant='simple'>
        <Tbody>
          {children}
        </Tbody>
      </Table>
    </TableContainer>
  )
}


type IFormField = { label: string, type: string, placeholder: string, isRequired: boolean, options: string, helperText: string }

export function FormFieldsInputControl({ field, fieldFormat = field, label, action, getValues, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false, }, setValue, ...rest }: FormFieldsInputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)

  const defaultFields: IFormField = {
    label: 'Label',
    type: '',
    placeholder: '',
    helperText: '',
    options: '',
    isRequired: false
  }

  console.log("FormField test: ", getValues ? getValues(field) : []);


  const [formFields, setFormFields] = useState<IFormField[]>(getValues && typeof getValues(field) === "object" && getValues(field).length > 0 ? getValues(field) : [
    defaultFields
  ])

  useEffect(() => {
    if (formFields) {
      setValue(field, formFields)
    }
  }, [formFields, setValue, field])

  const onAddField = () => {
    const getFieldsValue = getValues ? getValues(field) : []
    const fields = [...getFieldsValue, defaultFields]
    setFormFields(fields)
  }

  function CustomWrapper({ children, action }: { children: React.ReactNode, action?: 'show' | 'create' | 'edit' }) {
    return (
      <>
        {action === "show" ? (
          <Tr>
            <Td colSpan={2}>
              {children}
            </Td>
          </Tr>
        ) : (
          <>
            {children}
          </>
        )}
      </>
    )
  }

  const MemoizedFormFieldInput = memo(FormFieldInput)
  const onSetFormFields = useCallback((fields: IFormField[]) => {
    setValue(field, fields)
  }, [setValue, field])

  return (
    <>
      <CustomWrapper action={action}>
        <>
          {action !== 'show' && (
            <>
              <hr />
              <FormLabel mt={2}>Champs du formulaire: </FormLabel>
            </>
          )}
          <Box p={action !== 'show' ? 2 : 0}>
            <Accordion allowMultiple gap={2}>
              {formFields && formFields.map((field, index) => (
                <div key={index}>
                  <MemoizedFormFieldInput field={field} index={index} formFields={formFields} onSetFormFields={onSetFormFields} action={action} />
                </div>
              ))}
            </Accordion>
            {action !== 'show' && (
              <Button variant="link" colorScheme="brand" onClick={onAddField}>Ajouter un autre champ de formulaire</Button>
            )}
          </Box>
        </>
      </CustomWrapper>
    </>
  )
}

function FormFieldInput({ index, field, formFields, onSetFormFields, action }: { index: number, field: IFormField, action?: 'create' | 'show' | 'edit', formFields: IFormField[], onSetFormFields: (fields: IFormField[]) => void }) {

  const [formLabel, setFormLabel] = useState(field?.label?.length > 0 ? field?.label : '')
  const [formType, setFormType] = useState(field?.type?.length > 0 ? field?.type : 'text')
  const [formPlaceholder, setFormPlaceholder] = useState(field?.placeholder?.length > 0 ? field?.placeholder : '')
  const [formFieldHelper, setFormFieldHelper] = useState(field?.helperText?.length > 0 ? field?.helperText : '')
  const [formRequired, setFormRequired] = useState(field?.isRequired ?? true)


  const [accordionState, setAccordionState] = useState(false)

  // const [formOptions, setFormOptions] = useState<{ id: string, name: string }[]>([])
  const [textOptions, setTextOptions] = useState(field?.options?.length > 0 ? field?.options : '')

  // useEffect(() => {
  //   if (textOptions.length > 0) {
  //     const valOptions = textOptions.split(',')
  //     const options: { id: string, name: string }[] = valOptions.map((o) => ({ id: o, name: o }))
  //     setFormOptions(options)
  //   }

  // }, [textOptions])

  const onChangeFormField = (field: 'label' | 'type' | 'placeholder' | 'isRequired' | 'helperText' | 'options', index: number, value: string | boolean) => {
    console.log('formFields begin: ', formFields);

    let fields = [...formFields]
    let fieldData = fields[index]

    switch (field) {
      case 'label':
        setFormLabel(value as string)
        fieldData.label = value as string
        break;

      case 'type':
        setFormType(value as string)
        fieldData.type = value as string
        break;

      case 'placeholder':
        setFormPlaceholder(value as string)
        fieldData.placeholder = value as string
        break;

      case 'isRequired':
        setFormRequired(value as boolean)
        fieldData.isRequired = value as boolean
        break;

      case 'helperText':
        setFormFieldHelper(value as string)
        fieldData.helperText = value as string
        break;

      case 'options':
        setTextOptions(value as string)
        fieldData.options = value as string
        break;
    }

    onSetFormFields(fields)
    console.log('formFields end: ', formFields);
  }

  return (
    <Box mb={5}>
      {/* <FormControl isRequired={formRequired} >
        <FormLabel>{formLabel}</FormLabel>
        <MyInput />
        <FormHelperText>{formFieldHelper}</FormHelperText>
      </FormControl> */}

      <SingleFormFieldInputControl formType={formType} formLabel={formLabel} formFieldHelper={formFieldHelper} textOptions={textOptions} formPlaceholder={formPlaceholder} action={action} formRequired={formRequired} />

      {action !== 'show' && (
        <AccordionItem border={1} borderStyle={"solid"} borderColor={"gray.100"} borderRadius={7} mt={1}>
          <AccordionButton bg="gray.100" _hover={{ bg: "gray.200" }} fontWeight="bold" color="gray.500">
            OPTIONS
          </AccordionButton>
          <AccordionPanel>

            <Flex direction="column" gap={3}>
              <FormControl>
                <FormLabel>Label</FormLabel>
                <Input value={formLabel} onChange={(e) => onChangeFormField('label', index, e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Type</FormLabel>
                <Select value={formType} onChange={(e) => onChangeFormField('type', index, e.target.value)}>
                  <option value="checkbox">Case à cocher</option>
                  <option value="date">Date</option>
                  <option value="time">Heure</option>
                  <option value="password">Mot de passe</option>
                  <option value="number">Nombre</option>
                  <option value="radio">Radio</option>
                  <option value="select">Select</option>
                  <option value="tel">Tel</option>
                  <option value="text">Texte</option>
                  <option value="textarea">Texte Long</option>
                  <option value="url">Url</option>
                </Select>
              </FormControl>
              {(formType === "select" || formType === "checkbox" || formType === "radio") && (
                <FormControl>
                  <FormLabel>Options</FormLabel>
                  <Textarea value={textOptions} onChange={(e) => onChangeFormField('options', index, e.target.value)} placeholder="Option1; Option2; Option3..." />
                </FormControl>
              )}
              <FormControl>
                <FormLabel>Placeholder</FormLabel>
                <Input value={formPlaceholder} onChange={(e) => onChangeFormField('placeholder', index, e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>IsRequired</FormLabel>
                <Checkbox isChecked={formRequired} onChange={(e) => onChangeFormField('isRequired', index, e.target.checked)} />
              </FormControl>
              <FormControl>
                <FormLabel>HelperText</FormLabel>
                <Input value={formFieldHelper} onChange={(e) => onChangeFormField('helperText', index, e.target.value)} />
              </FormControl>

            </Flex>
          </AccordionPanel>
        </AccordionItem>
      )}
    </Box>
  )
}

interface SingleFormFieldInputControlProps {
  formType: string;
  textOptions: string;
  formPlaceholder: string;
  action?: 'show' | 'create' | 'edit';
  formRequired: boolean;
  formLabel: string;
  formFieldHelper: string;
  fieldId?: string | number
}

export function SingleFormFieldInputControl({ formType, fieldId, textOptions, formPlaceholder, action, formRequired, formLabel, formFieldHelper }: SingleFormFieldInputControlProps) {

  // const [formOptions, setFormOptions] = useState<{ id: string, name: string }[]>([])
  // const [textOptions, setTextOptions] = useState(field?.options?.length > 0 ? field?.options : '')

  let formOptions: { id: string, name: string }[] = []

  if (textOptions?.length > 0) {
    const valOptions = textOptions.split(';')
    const options: { id: string, name: string }[] = valOptions.map((o) => ({ id: o, name: o }))
    formOptions = options
  }

  const MyInput = () => {
    switch (formType) {
      case 'checkbox':
        return (
          <>
            {formOptions.map((o) => (
              <HStack key={o.id}>
                <Checkbox name={fieldId?.toString()} value={o.id} placeholder={formPlaceholder} isReadOnly={action === 'show'} />
                <FormLabel>{o.id}</FormLabel>
              </HStack>
            ))}
          </>
        )

      case 'radio':
        return (
          <>
            {formOptions.map((o) => (
              <HStack key={o.id}>
                <Radio name={fieldId?.toString()} value={o.id} placeholder={formPlaceholder} isReadOnly={action === 'show'} />
                <FormLabel>{o.id}</FormLabel>
              </HStack>
            ))}
          </>
        )

      case 'select':
        return (
          <Select name={fieldId?.toString()} placeholder={formPlaceholder} isReadOnly={action === 'show'}>
            {formOptions.map((o) => (
              <option key={o.id} value={o.id}> {o.name} </option>
            ))}
          </Select>
        )

      case 'textarea':
        return (
          <Textarea name={fieldId?.toString()} placeholder={formPlaceholder} isReadOnly={action === 'show'} />
        )

      default:
        return <Input name={fieldId?.toString()} type={formType} placeholder={formPlaceholder} isReadOnly={action === 'show'} />

    }
  }

  return (
    <FormControl isRequired={formRequired} >
      <FormLabel>{formLabel}</FormLabel>
      <MyInput />
      <FormHelperText>{formFieldHelper}</FormHelperText>
    </FormControl>
  )
}

export function JsonInputControl({ options, isStacked = false, field, fieldFormat = field, label, action, getValues, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false }, ...rest }: SelectInputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)

  const data = getValues && typeof getValues(field) === "object" ? getValues(field) : {}

  function getLabel(key: any) {
    const filteredOption = (options.filter((o) => o?.id == key))[0]
    if ('label' in filteredOption) {
      return filteredOption?.label
    }
  }

  return (
    <>
      {action !== "show" ? (
        <div></div>
      ) : (
        <>
          {Object.entries(data).map(([key, values]: any) => (
            <Tr key={key}>
              <Th>
                {getLabel(key)}
              </Th>
              <Td>{values}</Td>
            </Tr>
          ))}
        </>
      )}
    </>
  )
}

export function ImagesInputControl({ field, fieldFormat = field, label, action, getValues, isRequired = true, register, errors, validation = { required: isRequired ? 'Veuillez renseigner votre ' + fieldFormat : false, }, setValue, ...rest }: FormFieldsInputControlProps) {
  const fieldFormatUpper = fieldFormat.charAt(0).toString().toUpperCase() + fieldFormat.slice(1)
  return (
    <>
      {action !== "show" ? (
        <FormControl mb={4} id={field} isInvalid={!!errors[field]} isRequired={isRequired}>
          <FileUploader setValue={setValue} field={field} />
          {/* <FormLabel>{label ?? fieldFormatUpper}</FormLabel>
          <chakra.input
            id={field + action}
            type="file"
            w="100%"
            p="2"
            borderColor="grey.100"
            borderRadius="md"
            textColor="gray.500"
            borderWidth="1px"
            as={"input"}
            css={{
              "&::file-selector-button": {
                alignItems: "center",
                textAlign: "center",
                display: "none",
                backgroundColor: "blue.400",
                _hover: {
                  backgroundColor: "blue.500",
                },
                _active: {
                  backgroundColor: "blue.600",
                },
              },
            }}
            // placeholder="Suyash"
            // accept="image/*, video/*"
            // multiple={false}
            {...register(field, { ...validation })}
            {...rest}
          /> */}
          <FormErrorMessage>
            {/* {!!error && error.message} AND */}
            {!!errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      ) : (
        <Tr>
          <Td colSpan={2}>{!!getValues && <Image src={`${process?.env?.NEXT_PUBLIC_DIRECTUS_URL}/assets/${getValues("filename_disk")?.toString()}`} alt={'Show the ' + getValues("filename_download")?.toString() + 'file'} width={200} height={150} />}</Td>
        </Tr>
      )}
    </>
  )
}


const FileUploader = ({ setValue, field }: { field: DataFieldType, setValue: UseFormSetValue<any> }) => {

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dataLength, setDataLength] = useState<number>(0);

  const handleButtonClick = () => {
    setSelectedFile(null);
    setDataLength(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = event.target.files;
    if (uploadedFiles) {
      setSelectedFile(uploadedFiles?.[0])
      setValue(field, uploadedFiles)
      console.log("UploadedFiles: ", uploadedFiles);

      // I am reading an excel sheet and storing the length
    }
  };
  return (
    <Flex direction="column" width="400px" height="300px" border="2px dashed gray" borderRadius={20} alignItems="center" justifyContent="center">
      <Flex flex={1} justifyContent={"center"} alignItems={"center"}>
        <Button leftIcon={<MdOutlineFileUpload className="text-white w-5 h-5" />} shadow={"xl"} backgroundColor="gray.700" _hover={{ backgroundColor: 'gray.800' }} _active={{ backgroundColor: "gray.900" }} onClick={handleButtonClick}>
          <Text color="white">TELEVERSER</Text>
        </Button>
      </Flex>
      {selectedFile ? <Flex height={"75px"} p={5} borderTop={"1px dashed silver"} w={"100%"} justifyContent={"space-between"} alignItems={"center"}>
        <Flex direction={"column"}>
          <Heading fontSize={"24px"}>{selectedFile.name}</Heading>
          <Heading size="sm" fontFamily={"mono"} color={"gray.500"}>THIS FILE WILL ADD {dataLength} DEVICE{dataLength === 1 ? '' : 'S'}</Heading>
        </Flex>
      </Flex> : <Flex height={"75px"} alignItems={"center"} p={5} borderTop={"1px dashed silver"} w={"100%"}>
        <Heading size={"sm"} fontFamily={"mono"} color={"gray.400"} textAlign={"center"}>VOUS AVEZ LA POSSIBILITE DE TELEVERSER PLUSIEURS FICHIERS</Heading>
      </Flex>
      }
      <input type="file" accept="image/*, video/*" onChange={handleFileChange} ref={fileInputRef} style={{ display: 'none' }} multiple />
    </Flex>
  )
}